/**
 * whyLines.js — Feb 2026 · "Why" reveals for every activity completion.
 *
 * Emergent Command · Learning Strategy Upgrades (2): show a one-line
 * concept-visible sentence in Compass's voice immediately after the
 * usual process feedback. Highest-leverage, lowest-cost pedagogy
 * change — makes understanding literally visible.
 *
 * Keyed by skill-tag or activity-id. Falls back to a warm generic
 * line so a blank whyLine never renders. Editable in localStorage
 * (LS_KEY) so an admin panel can extend the map without a rebuild.
 */

const LS_KEY = "we_why_lines_v1";

// Seed library — hand-crafted concept sentences. Extend anytime.
const SEED = {
  // Maths
  "addition-facts":   "You added by making the next ten — that's counting on.",
  "subtraction-facts":"You took away and checked with addition — inverse operations.",
  "place-value":      "You saw that a 3 in the tens place means thirty, not three.",
  "number-sense":     "You compared how big two numbers feel — that's number sense.",
  "num-mult-2-times": "You skip-counted in twos — that's multiplication as repeated addition.",
  "num-place-value-100": "You regrouped ten ones into one ten — that's carrying.",
  "fractions":        "You cut a whole into equal parts — that's what a fraction is.",
  "measurement":      "You used the same unit for both — that's how measurement stays fair.",

  // Reading
  "phonics":          "You blended the sounds into a word — that's decoding.",
  "phonemic-awareness":"You heard the smallest bit of sound — that's a phoneme.",
  "blending":         "You slid the sounds together smoothly — that's blending.",
  "letter-sounds":    "You matched a letter to a sound — that's the alphabetic principle.",
  "sight-words":      "You read the whole word at a glance — that's a sight word.",
  "main-idea":        "You spotted what the paragraph was mostly about — that's the main idea.",
  "inference":        "You read between the lines — that's inference.",

  // Science
  "hypothesis":       "You guessed and then checked — that's a hypothesis.",
  "fair-test":        "You changed only one thing at a time — that's a fair test.",
  "living-things":    "You noticed a thing that grows, breathes and reproduces — that's what alive means.",

  // Geography
  "continents":       "You named a landmass surrounded by ocean — that's a continent.",
  "climate":          "You told weather (today) from climate (long-run patterns).",

  // Default per subject
  "__english":        "You practised looking closely at words — that's how reading grows.",
  "__maths":          "You worked with numbers as ideas, not just symbols — that's mathematics.",
  "__science":        "You looked at the world carefully and asked why — that's science.",
  "__geography":      "You placed a small thing on a bigger picture — that's geography.",

  // Absolute fallback.
  "__default":        "Every small step makes the next step easier. Nicely done.",
};

function _load() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); }
  catch { return {}; }
}

/** Look up the whyLine for a skill tag or subject. */
export function getWhyLine({ skill = "", subject = "" } = {}) {
  const custom = _load();
  const bag = { ...SEED, ...custom };
  const key = String(skill || "").toLowerCase();
  // Exact hit.
  if (bag[key]) return bag[key];
  // Prefix hit — "phonics-cvc" → "phonics".
  const prefixKey = Object.keys(bag).find((k) => k && !k.startsWith("__") && key.startsWith(k));
  if (prefixKey) return bag[prefixKey];
  // Subject default.
  const subj = String(subject || "").toLowerCase();
  if (subj && bag[`__${subj}`]) return bag[`__${subj}`];
  // Absolute fallback — never blank.
  return bag.__default;
}

/** Set / override a whyLine at runtime. Persists across sessions. */
export function setWhyLine(skill, sentence) {
  if (!skill || !sentence) return;
  const custom = _load();
  custom[String(skill).toLowerCase()] = String(sentence);
  try { localStorage.setItem(LS_KEY, JSON.stringify(custom)); }
  catch { /* ignore */ }
}

/** List every known whyLine (seed + custom). Useful for an admin
 *  editor. */
export function listWhyLines() {
  return { ...SEED, ..._load() };
}
